// Define all the types used in the application

export const ALL_POSITIONS = ['UTG', 'UTG+1', 'UTG+2', 'LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB'] as const;

export type PlayerPosition = typeof ALL_POSITIONS[number];

export const ACTION_LABELS: Record<PokerAction, string> = {
   'F': 'Fold',
   'C': 'Call',
   'R2': 'Raise 2x',
   'R2.3': 'Raise 2.3x',
   'R2.5': 'Raise 2.5x',
   'R3': 'Raise 3x',
   'R6.5': 'Raise 6.5x',
   'R7.5': 'Raise 7.5x',
   'R10': 'Raise 10x',
   'R11': 'Raise 11x',
   'R11.5': 'Raise 11.5x',
   'R12': 'Raise 12x',
   'R12.3': 'Raise 12.3x',
   'R13': 'Raise 13x',
   'R13.5': 'Raise 13.5x',
   'R19': 'Raise 19x',
   'R18': 'Raise 18x',
   'R20': 'Raise 20x',
   'R22': 'Raise 22x',
   'R24.9': 'Raise 24.9x',
   'R21': 'Raise 21x',
   'R25': 'Raise 25x',
   'R28': 'Raise 28x',
   'R26': 'Raise 26x',
   'R27': 'Raise 27x',
   'RAI': 'All-in'
};

export type PokerAction = 'F' | 'C' | 'R2' | 'R2.3' | 'R2.5' | 'R3' | 'R6.5' | 'R7.5' | 'R10' | 'R12' | 'R11' | 'R12.3' | 'R11.5' | 'R13.5' | 'R13' | 'R19' | 'R18' | 'R20' | 'R22' | 'R24.9' | 'R21' | 'R25' | 'R28' | 'R26' | 'R27' | 'RAI';

export type PokerPlayerAction = 
{
  action: PokerAction;
  actor: 'AI' | 'USER';
}

export type ActionsTotalFrequencies = {
   F: number;
   C: number;
   R2?: number;
   "R2.3"?: number;
   "R2.5"?: number;
   R3?: number;
   "R6.5"?: number;
   "R7.5"?: number;
   R10?: number;
   R11?: number;
   "R11.5"?: number;
   R12?: number;
   "R12.3"?: number;
   R13?: number;
   "R13.5"?: number;
   R19?: number;
   R18?: number;
   R20?: number;
   R22?: number;
   "R24.9"?: number;
   R21?: number;
   R25?: number;
   R28?: number;
   R26?: number;
   R27?: number;
   RAI: number;
};

export interface HandRange {
   f: ActionsTotalFrequencies;
   hand_ev: number;
   [key: string]: any;
}

export type RangeData = Record<string, HandRange>;

export interface RangeFile {
   players_info: Array<{
      simple_hand_counters: RangeData;
   }>;
}

export type Rank = 'A' | 'K' | 'Q' | 'J' | 'T' | '9' | '8' | '7' | '6' | '5' | '4' | '3' | '2';
export type Suit = 'H' | 'D' | 'C' | 'S';

export interface Card {
   rank: Rank;
   suit: Suit;
}

export type PlayerStatus = 'active' | 'folded' | 'raised' | 'called' | 'all-in';

export interface Player {
   position: PlayerPosition;
   isUser: boolean;
   hand?: Card[]; // Two cards with defined rank and suit
   status: PlayerStatus;
   chipStack: number;
   betAmount: number;
}

export type DifficultyMode = 'Simplified' | 'Hard';

export interface GameState {
   players: Player[];
   userPosition: PlayerPosition | null;
   randomizeUserPosition: boolean;
   openerPosition: PlayerPosition | null;
   threeBetPosition: PlayerPosition | null;
   potSize: number;
   currentPlayerIndex: number;
   userHand: Card[];
   difficultyMode: DifficultyMode;
   playerActions: PokerPlayerAction[];
   currentCorrectActions: PokerAction[];
   isHandComplete: boolean;
   ranges: Record<string, RangeData>;
   raiseRanges: Record<string, Record<string, RangeData>>;
   fourBetRanges: Record<string, Record<string, RangeData>>;
   generatedHandRanges: Record<PlayerPosition, string[]>;
   deck: Card[]; // Track the current deck to avoid duplicates
   stats: TrainingStats; // Added training stats to game state
   handRangeWidth: number; // 0-100 value controlling the range of hands used
   handId?: number; // Unique identifier for each hand, used to detect new hands
}

export interface TrainingStats {
   totalActions: number;
   correctDecisions: number;
   roundNumber: number;
   roundsTotal: number;
}

export interface RangeDataset {
   ranges: Record<string, RangeData>;
   raiseRanges: Record<string, Record<string, RangeData>>;
   fourBetRanges: Record<string, Record<string, RangeData>>;
}
